import { useEffect, useState } from "react";
import { Link } from "react-router";
import { LayoutDashboard, Clock, Zap, ArrowRight } from "lucide-react";
import { apiFetch } from "../api/client";
import { useWorkspace } from "../contexts/WorkspaceContext";

type Analytics = {
  total_seconds: number;
  focus_sessions_count: number;
  top_project: { project_id: string; name: string; seconds: number } | null;
  by_day: Array<{ date: string; seconds: number }>;
  by_project: Array<{ project_id: string; name: string; seconds: number }>;
};

type TimeEntry = {
  id: string;
  description: string;
  started_at: string;
  duration_seconds: number;
  source: "manual" | "timer";
};

type ActiveSession = {
  id: string;
  planned_minutes: number;
  started_at: string;
};

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function toLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function Dashboard() {
  const { activeWorkspace } = useWorkspace();
  const [todayStats, setTodayStats] = useState<Analytics | null>(null);
  const [weekStats, setWeekStats] = useState<Analytics | null>(null);
  const [recentEntries, setRecentEntries] = useState<TimeEntry[]>([]);
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setActiveSession(null);
      const today = toLocalDate(new Date());
      const weekAgo = toLocalDate(new Date(Date.now() - 7 * 86400000));
      const [todayRes, weekRes, entriesRes, activeRes] = await Promise.all([
        apiFetch("/analytics/me?period=today"),
        apiFetch("/analytics/me?period=week"),
        apiFetch(`/time-entries?from=${weekAgo}&to=${today}`),
        apiFetch("/timer/active"),
      ]);
      if (cancelled) return;
      if (todayRes.ok) setTodayStats(await todayRes.json());
      else setTodayStats(null);
      if (weekRes.ok) setWeekStats(await weekRes.json());
      else setWeekStats(null);
      if (entriesRes.ok) {
        const all: TimeEntry[] = await entriesRes.json();
        setRecentEntries(all.slice(0, 5));
      } else {
        setRecentEntries([]);
      }
      if (activeRes.ok) {
        const data = await activeRes.json();
        if (data && data.id) setActiveSession(data);
      }
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [activeWorkspace]);

  if (loading) {
    return <div className="text-ink-2 text-sm">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
        <LayoutDashboard size={22} className="text-brand" />
        Dashboard
      </h1>

      {activeSession && (
        <div className="bg-brand/10 border border-brand/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap size={18} className="text-brand shrink-0" />
            <div>
              <p className="text-brand text-sm font-medium">Focus session active</p>
              <p className="text-ink-2 text-sm">{activeSession.planned_minutes}min session</p>
            </div>
          </div>
          <Link
            to="/timer"
            className="bg-brand hover:bg-brand-2 text-white text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            Go to Timer <ArrowRight size={14} />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
          <div className="flex items-center gap-2 text-ink-2 text-sm mb-3">
            <Clock size={14} />
            Today
          </div>
          <p className="text-3xl font-bold text-ink">
            {todayStats ? formatDuration(todayStats.total_seconds) : "0m"}
          </p>
          <p className="text-ink-3 text-sm mt-1">
            {todayStats?.focus_sessions_count ?? 0} focus sessions
          </p>
        </div>
        <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
          <div className="flex items-center gap-2 text-ink-2 text-sm mb-3">
            <Clock size={14} />
            This week
          </div>
          <p className="text-3xl font-bold text-ink">
            {weekStats ? formatDuration(weekStats.total_seconds) : "0m"}
          </p>
          <p className="text-ink-3 text-sm mt-1">
            {weekStats?.focus_sessions_count ?? 0} focus sessions
          </p>
        </div>
      </div>

      <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-ink">Recent entries</h2>
          <Link to="/timesheets" className="text-brand hover:opacity-80 text-sm transition-opacity flex items-center gap-1">
            View all <ArrowRight size={13} />
          </Link>
        </div>
        {recentEntries.length === 0 ? (
          <p className="text-ink-3 text-sm">No entries yet.</p>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {recentEntries.map((entry) => (
              <div key={entry.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-ink-2 text-sm">
                    {entry.description || "No description"}
                  </p>
                  <p className="text-ink-3 text-xs mt-0.5">
                    {new Date(entry.started_at).toLocaleDateString()} · {entry.source}
                  </p>
                </div>
                <p className="text-ink-2 text-sm shrink-0 ml-4">
                  {formatDuration(entry.duration_seconds)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
